import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

const SELLING_PLAN_SYNC_EVENT = 'selling-plan:sync';
const SELLING_PLAN_SYNC_SOURCE_STICKY = 'sticky-add-to-cart';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 * @property {HTMLSelectElement | undefined} sellingPlanSelect - Sticky selling plan select
 * @property {HTMLElement | undefined} stickyPrice - Sticky price wrapper
 * @property {HTMLElement | undefined} stickyPriceCurrent - Sticky current price text
 * @property {HTMLElement | undefined} stickyPriceCompare - Sticky compare price text
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {AbortController | null} */
  #stickyRenderAbortController = null;

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {HTMLElement | Document} */
  #syncScope = document;

  /**
   * Builds the sticky subscription label from the active comparison card.
   * @returns {string}
   */
  #getActiveSubscriptionPlanLabel() {
    const activeRadio = this.#syncScope.querySelector(
      '.subscription-plan-comparison__plan-container .subscription-plan-comparison__radio.is-active'
    );
    if (!(activeRadio instanceof HTMLElement)) return '';

    const planContainer = activeRadio.closest('.subscription-plan-comparison__plan-container');
    if (!(planContainer instanceof HTMLElement)) return '';

    const totalDays = `${planContainer.dataset.totalDays || ''}`.trim();
    const saveBadgeText = planContainer
      .querySelector('.subscription-plan-comparison__save-badge')
      ?.textContent?.replace(/\s+/g, ' ')
      .trim();
    const saveMatch = saveBadgeText?.match(/SAVE\s+(\d+%)/i);

    if (totalDays && saveMatch?.[1]) {
      return `${totalDays} day Supply | Save ${saveMatch[1]}`;
    }

    const titleElement = planContainer.querySelector('.subscription-plan-comparison__title');
    return titleElement instanceof HTMLElement ? titleElement.textContent?.trim() || '' : '';
  }

  /**
   * Reapplies sticky subscription copy after sticky bar morphs or selling plan syncs.
   */
  #syncStickyPlanLabel() {
    const title = this.#getActiveSubscriptionPlanLabel();
    if (!title) return;

    const planTitleElements = this.querySelectorAll('[data-sticky-plan-title]');
    planTitleElements.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.textContent = title;
      }
    });
  }

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    this.addEventListener('change', this.#handleChange, { signal });

    this.#syncScope = this.closest('.shopify-section, dialog, product-card') || document;
    this.#syncScope.addEventListener(SELLING_PLAN_SYNC_EVENT, this.#handleExternalSellingPlanSync, { signal });
    this.#syncScope.addEventListener('change', this.#handleSellingPlanInputChange, { signal });

    this.#getInitialQuantity();
    this.#syncSellingPlanFromInput();
    queueMicrotask(() => {
      this.#syncStickyPlanLabel();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    this.#stickyRenderAbortController?.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          // Element is above viewport - show sticky bar
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;

    const selectedSellingPlan = this.refs.sellingPlanSelect instanceof HTMLSelectElement ? this.refs.sellingPlanSelect.value : '';
    if (selectedSellingPlan) {
      this.#applySellingPlanSelection(selectedSellingPlan, {
        shouldUpdateInput: true,
        shouldDispatchInputChange: false,
        shouldBroadcast: false,
      });
    }

    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = `${event.detail.data.newProduct.id || this.dataset.productId}`;
    } else if (`${event.detail.data.productId || ''}` !== `${this.dataset.productId || ''}`) {
      return;
    }

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();

    queueMicrotask(() => {
      this.#syncSellingPlanFromInput({ shouldUpdateInput: true, shouldDispatchInputChange: true, shouldBroadcast: true });
      this.#syncStickyPlanLabel();
    });
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Handles internal sticky select changes.
   * @param {Event} event
   */
  #handleChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement) || target !== this.refs.sellingPlanSelect) return;

    this.#applySellingPlanSelection(target.value, {
      shouldUpdateInput: true,
      shouldDispatchInputChange: true,
      shouldBroadcast: true,
    });
  };

  /**
   * Handles shared selling plan sync events from other components.
   * @param {CustomEvent} event
   */
  #handleExternalSellingPlanSync = async (event) => {
    if (!event?.detail) return;

    if (event.detail.source === SELLING_PLAN_SYNC_SOURCE_STICKY) return;
    if (`${event.detail.productId || ''}` !== `${this.dataset.productId || ''}`) return;

    const incomingVariantId = `${event.detail.variantId || ''}`;
    const incomingSellingPlanId = `${event.detail.sellingPlanId || ''}`;

    if (incomingVariantId && `${this.dataset.currentVariantId || ''}` !== incomingVariantId) {
      const requestedVariantChange = this.#requestVariantChange(incomingVariantId);
      if (requestedVariantChange) return;

      const renderedStickyVariant = await this.#renderStickyForVariant(incomingVariantId, incomingSellingPlanId);
      if (renderedStickyVariant) return;
    }

    this.#applySellingPlanSelection(incomingSellingPlanId, {
      shouldUpdateInput: false,
      shouldDispatchInputChange: false,
      shouldBroadcast: false,
    });
  };

  /**
   * Keeps sticky UI in sync when the linked selling_plan input changes.
   * @param {Event} event
   */
  #handleSellingPlanInputChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.name !== 'selling_plan') return;

    const sellingPlanInputs = this.#getSellingPlanInputsForProductForm();
    if (!sellingPlanInputs.includes(target)) return;

    this.#syncSellingPlanFromInput();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Requests a variant picker change when subscription sync points to another variant.
   * @param {string} variantId
   * @returns {boolean}
   */
  #requestVariantChange(variantId) {
    if (!variantId) return false;
    if (`${this.dataset.currentVariantId || ''}` === variantId) return false;

    const picker =
      this.#syncScope.querySelector(`variant-picker[data-product-id="${this.dataset.productId || ''}"]`) ||
      this.#syncScope.querySelector('variant-picker');
    if (!(picker instanceof HTMLElement)) return false;

    const candidates = Array.from(picker.querySelectorAll('[data-variant-id]')).filter(
      (element) => element instanceof HTMLElement && `${element.dataset.variantId || ''}` === variantId
    );
    if (candidates.length === 0) return false;

    const isSelectedCandidate = (candidate) => {
      if (candidate instanceof HTMLInputElement) return candidate.checked;
      if (candidate instanceof HTMLOptionElement) return candidate.selected;
      return false;
    };

    const candidate = candidates.find((entry) => !isSelectedCandidate(entry)) || candidates[0];
    if (candidate instanceof HTMLInputElement) {
      if (!candidate.checked) {
        candidate.click();
      } else {
        candidate.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return true;
    }

    if (candidate instanceof HTMLOptionElement) {
      const select = candidate.parentElement;
      if (!(select instanceof HTMLSelectElement)) return false;

      if (select.value !== candidate.value || !candidate.selected) {
        select.value = candidate.value;
        candidate.selected = true;
      }
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    return false;
  }

  /**
   * Fallback: render sticky bar for an incoming variant using section rendering URL.
   * @param {string} variantId
   * @param {string} sellingPlanId
   * @returns {Promise<boolean>}
   */
  async #renderStickyForVariant(variantId, sellingPlanId = '') {
    const sectionElement = this.closest('.shopify-section');
    const sectionId = sectionElement?.id?.replace('shopify-section-', '') || '';
    if (!sectionId) return false;

    this.#stickyRenderAbortController?.abort();
    const requestAbortController = new AbortController();
    this.#stickyRenderAbortController = requestAbortController;

    try {
      const requestUrl = new URL(window.location.href);
      requestUrl.searchParams.set('section_id', sectionId);
      requestUrl.searchParams.set('variant', variantId);

      const response = await fetch(requestUrl.toString(), {
        signal: requestAbortController.signal,
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) return false;

      const responseText = await response.text();
      const parsed = new DOMParser().parseFromString(responseText, 'text/html');
      const newStickyAddToCart = parsed.querySelector('sticky-add-to-cart');
      const newStickyBar = newStickyAddToCart?.querySelector('[ref="stickyBar"]');
      if (!(newStickyAddToCart instanceof HTMLElement) || !(newStickyBar instanceof HTMLElement)) return false;

      const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
      morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });
      this.refs.stickyBar.setAttribute('data-stuck', currentStuck);

      this.dataset.variantAvailable = newStickyAddToCart.dataset.variantAvailable || this.dataset.variantAvailable;
      this.dataset.currentVariantId = variantId;

      const productForm = this.#getProductForm();
      const variantInput = productForm?.querySelector('input[name="id"]');
      if (variantInput instanceof HTMLInputElement) {
        variantInput.value = variantId;
      }
      if (productForm) {
        this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
      }

      if (sellingPlanId) {
        this.#applySellingPlanSelection(sellingPlanId, {
          shouldUpdateInput: true,
          shouldDispatchInputChange: true,
          shouldBroadcast: false,
        });
      } else {
        this.#syncSellingPlanFromInput();
      }

      queueMicrotask(() => {
        this.#syncStickyPlanLabel();
      });

      return true;
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.error('Failed to render sticky add to cart for variant:', error);
      }
      return false;
    } finally {
      if (this.#stickyRenderAbortController === requestAbortController) {
        this.#stickyRenderAbortController = null;
      }
    }
  }

  /**
   * @param {{ createMissing?: boolean }} [options]
   * @returns {HTMLInputElement[]}
   */
  #getSellingPlanInputsForProductForm(options = {}) {
    const { createMissing = false } = options;
    const productForm = this.#getProductForm();
    const form = productForm?.querySelector('form');
    if (!(form instanceof HTMLFormElement)) return [];

    const inputs = [];

    if (form.id) {
      const linkedInputs = Array.from(this.#syncScope.querySelectorAll(`input[name="selling_plan"][form="${form.id}"]`));
      linkedInputs.forEach((input) => {
        if (input instanceof HTMLInputElement) {
          inputs.push(input);
        }
      });
    }

    const formInputs = Array.from(form.querySelectorAll('input[name="selling_plan"]'));
    formInputs.forEach((input) => {
      if (input instanceof HTMLInputElement && !inputs.includes(input)) {
        inputs.push(input);
      }
    });

    if (inputs.length === 0 && createMissing) {
      const fallbackInput = document.createElement('input');
      fallbackInput.type = 'hidden';
      fallbackInput.name = 'selling_plan';
      form.appendChild(fallbackInput);
      inputs.push(fallbackInput);
    }

    return inputs;
  }

  /**
   * @param {string} sellingPlanId
   * @returns {string}
   */
  #normalizeSellingPlanId(sellingPlanId) {
    const select = this.refs.sellingPlanSelect;
    if (!(select instanceof HTMLSelectElement) || select.options.length === 0) return '';

    const hasSellingPlan = Array.from(select.options).some((option) => option.value === sellingPlanId);
    if (hasSellingPlan) return sellingPlanId;

    return select.options[0]?.value || '';
  }

  /**
   * @param {{ shouldUpdateInput?: boolean; shouldDispatchInputChange?: boolean; shouldBroadcast?: boolean }} [options]
   */
  #syncSellingPlanFromInput(options = {}) {
    const select = this.refs.sellingPlanSelect;
    if (!(select instanceof HTMLSelectElement)) {
      this.#updateStickyPrice('');
      this.#syncStickyPlanLabel();
      return;
    }

    const sellingPlanInputs = this.#getSellingPlanInputsForProductForm();
    const requestedSellingPlanId = sellingPlanInputs[0]?.value || select.value || '';

    this.#applySellingPlanSelection(requestedSellingPlanId, {
      shouldUpdateInput: options.shouldUpdateInput ?? false,
      shouldDispatchInputChange: options.shouldDispatchInputChange ?? false,
      shouldBroadcast: options.shouldBroadcast ?? false,
    });
  }

  /**
   * @param {string} sellingPlanId
   * @param {{ shouldUpdateInput?: boolean; shouldDispatchInputChange?: boolean; shouldBroadcast?: boolean }} [options]
   */
  #applySellingPlanSelection(sellingPlanId, options = {}) {
    const select = this.refs.sellingPlanSelect;
    if (!(select instanceof HTMLSelectElement)) {
      this.#updateStickyPrice('');
      this.#syncStickyPlanLabel();
      return;
    }

    const resolvedSellingPlanId = this.#normalizeSellingPlanId(sellingPlanId);

    if (select.value !== resolvedSellingPlanId) {
      select.value = resolvedSellingPlanId;
    }

    this.#updateStickyPrice(resolvedSellingPlanId);

    if (options.shouldUpdateInput) {
      const sellingPlanInputs = this.#getSellingPlanInputsForProductForm({ createMissing: true });
      let hasDispatchedChange = false;

      sellingPlanInputs.forEach((sellingPlanInput) => {
        const valueChanged = sellingPlanInput.value !== resolvedSellingPlanId;
        sellingPlanInput.value = resolvedSellingPlanId;

        if (options.shouldDispatchInputChange && valueChanged && !hasDispatchedChange) {
          hasDispatchedChange = true;
          sellingPlanInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    if (options.shouldBroadcast) {
      const productForm = this.#getProductForm();
      const variantInput = productForm?.querySelector('input[name="id"]');
      const selectedVariantId = variantInput instanceof HTMLInputElement ? variantInput.value : '';

      this.#syncScope.dispatchEvent(
        new CustomEvent(SELLING_PLAN_SYNC_EVENT, {
          bubbles: true,
          detail: {
            source: SELLING_PLAN_SYNC_SOURCE_STICKY,
            productId: this.dataset.productId || '',
            sellingPlanId: resolvedSellingPlanId,
            variantId: selectedVariantId,
          },
        })
      );
    }

    this.#syncStickyPlanLabel();
  }

  /**
   * @param {string} sellingPlanId
   */
  #updateStickyPrice(sellingPlanId) {
    const priceElement = this.refs.stickyPrice;
    const currentPrice = this.refs.stickyPriceCurrent;
    const comparePrice = this.refs.stickyPriceCompare;

    if (!(priceElement instanceof HTMLElement) || !(currentPrice instanceof HTMLElement)) return;

    let resolvedPrice = priceElement.dataset.defaultPrice || '';
    let resolvedComparePrice = priceElement.dataset.defaultComparePrice || '';

    const select = this.refs.sellingPlanSelect;
    if (sellingPlanId && select instanceof HTMLSelectElement) {
      const selectedOption = Array.from(select.options).find((option) => option.value === sellingPlanId);
      if (selectedOption instanceof HTMLOptionElement) {
        resolvedPrice = selectedOption.dataset.price || resolvedPrice;
        resolvedComparePrice = selectedOption.dataset.comparePrice || '';
      }
    }

    if (resolvedPrice) {
      currentPrice.textContent = resolvedPrice;
    }

    if (comparePrice instanceof HTMLElement) {
      if (resolvedComparePrice) {
        comparePrice.hidden = false;
        comparePrice.textContent = resolvedComparePrice;
      } else {
        comparePrice.hidden = true;
        comparePrice.textContent = '';
      }
    }
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
